import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { of } from 'rxjs';
import { delay, takeUntil } from 'rxjs/operators';
import {
  APP_MODE,
  Appointment,
  BusinessObject,
  MESSAGE_SENDER_TYPE,
} from '../../../models';
import { ErrorHandlerServiceService } from '../../../services';
import { StringUtils } from '../../../utils';
import { BaseComponent } from '../../../core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PipesModule } from '../../../pipes';
import { LibCoreLayoutModule } from '../../../ui/core-layout';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatInputModule } from '@angular/material/input';
import { CalendarService } from '../services/calendar.service';
import { CustomerNameFormatterPipe } from '../../../pipes/customer-name-formatter.pipe';

@Component({
  selector: 'lib-calendar-appointment-chat-box',
  templateUrl: './calendar-appointment-chat-box.component.html',
  styleUrls: ['./calendar-appointment-chat-box.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    PipesModule,
    LibCoreLayoutModule,
    // material design
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressBarModule,
  ],
})
export class CalendarAppointmentChatBoxComponent extends BaseComponent {
  private _appointment: Appointment;
  @Input() set appointment(value: Appointment) {
    this._appointment = value;
    this.scrollMessageContainerToBottom();
  }
  get appointment(): Appointment {
    return this._appointment;
  }

  @Input() mode: APP_MODE;

  @Input() set disable(value: boolean) {
    if (value) {
      this.inputControl.disable();
    } else {
      this.inputControl.enable();
    }
  }

  @Input() businessObject: BusinessObject;
  @Input() displayCloseButton: boolean;

  @Output() appointmentChange = new EventEmitter<Appointment>();
  @Output() closeClicked = new EventEmitter<any>();

  @ViewChild('messageContainer') private messageContainerRef: ElementRef;

  public isLoading = false;

  public inputControl = new FormControl();

  constructor(
    private errorHandlerServiceService: ErrorHandlerServiceService,
    private customerNameFormatterPipe: CustomerNameFormatterPipe,
    private translateService: TranslateService,
    private calendarService: CalendarService
  ) {
    super();
  }

  public getNameOfOpposite(): string {
    if (this.mode === APP_MODE.BOOKING) {
      return (
        this.businessObject?.name ||
        this.translateService.instant(
          'lib_calendar.appointment-chat-box.text.salon'
        )
      );
    }

    if (this.appointment?.owner) {
      return this.customerNameFormatterPipe.transform(this.appointment.owner);
    }

    return this.translateService.instant(
      'lib_calendar.appointment-chat-box.text.customer'
    );
  }

  public isOwnerOfMessage(messageSenderType: MESSAGE_SENDER_TYPE): boolean {
    if (this.mode === APP_MODE.BOOKING) {
      return messageSenderType === MESSAGE_SENDER_TYPE.CUSTOMER;
    } else {
      return messageSenderType === MESSAGE_SENDER_TYPE.BUSINESS;
    }
  }

  public isAbleToSendMessage(): boolean {
    if (this.inputControl.disabled) {
      return false;
    }

    const messageStr = StringUtils.simplifyString(this.inputControl.value);
    return (
      !this.isLoading &&
      messageStr?.length &&
      !!this.businessObject?.id &&
      !!this.appointment?.id
    );
  }

  public async writeMessage() {
    if (!this.isAbleToSendMessage()) {
      return;
    }

    try {
      this.isLoading = true;
      const appointmentMessage =
        await this.calendarService.writeAppointmentMessage(
          this.businessObject?.id,
          this.appointment?.id,
          this.inputControl.value?.trim()
        );

      if (appointmentMessage?.id) {
        this.inputControl.reset();
        const updatedAppointment =
          await this.calendarService.getAppointmentById(this.appointment.id);
        this.appointmentChange.emit(updatedAppointment);
        this.scrollMessageContainerToBottom();
      }
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    } finally {
      this.isLoading = false;
    }
  }

  private scrollMessageContainerToBottom(): void {
    try {
      of('')
        .pipe(takeUntil(this.componentDestroyed), delay(200))
        .subscribe(
          () =>
            (this.messageContainerRef.nativeElement.scrollTop =
              this.messageContainerRef.nativeElement.scrollHeight)
        );
    } catch (e) {}
  }
}
